import { useId } from 'react';

import { DataTestIds, RefundRequestReasonCode } from '@repo/frontend-shared';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSText } from '@repo/sds/SDSText';
import { SDSTextArea } from '@repo/sds/SDSTextArea';
import {
  type Option,
  SingleChoiceDropdown,
} from '@repo/ui/components/elements/SingleChoiceDropdown/SingleChoiceDropdown';

import { asksForReasonDetail, REFUND_REASON_DETAIL_MAX_LENGTH, REFUND_REASON_LABELS } from './refund-format';

/**
 * Hoisted so the dropdown is handed the same function on every render — it
 * treats `getOptions` as a fetch, and an inline arrow re-runs it each time.
 */
const REASON_OPTIONS: Option<RefundRequestReasonCode>[] = Object.values(RefundRequestReasonCode).map(reasonCode => ({
  id: reasonCode,
  name: REFUND_REASON_LABELS[reasonCode],
}));

function getReasonOptions(): Option<RefundRequestReasonCode>[] {
  return REASON_OPTIONS;
}

const REASON_LABEL = 'Reason for refund (optional)';
// The frame's words, in the member's voice.
const DETAIL_LABEL = 'Tell me a little more';

interface RefundReasonFieldsProps {
  reasonCode?: RefundRequestReasonCode;
  reasonDetail: string;
  isDisabled: boolean;
  onChangeReasonCode(reasonCode: RefundRequestReasonCode | undefined): void;
  onChangeReasonDetail(reasonDetail: string): void;
}

/**
 * A field's title as the refund frames set it — small and muted, quieter than
 * SDSInputLabel's body-large. The control carries the same words as its
 * accessible name, so nothing is lost by not using a <label> here.
 */
function FieldTitle({ text }: { text: string }) {
  return <SDSText type="body-medium" text={text} color="muted" />;
}

/**
 * Why the member is asking, captured for us rather than required of them.
 *
 * Both fields are optional — a refund inside the guarantee is not conditional
 * on explaining yourself, and gating the button on an answer would turn
 * feedback into a toll. The detail box appears only for "Other", the one
 * reason that needs words of its own.
 */
export function RefundReasonFields({
  reasonCode,
  reasonDetail,
  isDisabled,
  onChangeReasonCode,
  onChangeReasonDetail,
}: RefundReasonFieldsProps) {
  const counterId = useId();

  return (
    <SDSFlexbox direction="column" gap="no-design-24" fullWidth>
      <SDSFlexbox direction="column" gap="input-regular" alignItems="flex-start" fullWidth>
        <FieldTitle text={REASON_LABEL} />
        <SingleChoiceDropdown
          ariaLabel={REASON_LABEL}
          inputPlaceholder="Select a reason"
          getOptions={getReasonOptions}
          value={reasonCode ?? null}
          isDisabled={isDisabled}
          onChange={option => {
            onChangeReasonCode(option?.id);
          }}
          dataTestId={DataTestIds.BILLING.REFUND.REASON_SELECT}
        />
      </SDSFlexbox>

      {asksForReasonDetail(reasonCode) ? (
        <SDSFlexbox direction="column" gap="text-large" fullWidth>
          <SDSFlexbox direction="column" gap="text-small" alignItems="flex-start" fullWidth>
            <FieldTitle text={DETAIL_LABEL} />
            <SDSTextArea
              ariaLabel={DETAIL_LABEL}
              placeholder="Briefly describe your reason for requesting a refund"
              value={reasonDetail}
              onChange={onChangeReasonDetail}
              isDisabled={isDisabled}
              isFullWidth
              rows={4}
              maxLength={REFUND_REASON_DETAIL_MAX_LENGTH}
              ariaDescribedBy={counterId}
              dataTestId={DataTestIds.BILLING.REFUND.REASON_DETAIL}
            />
          </SDSFlexbox>
          <SDSFlexbox direction="row" justifyContent="space-between" alignItems="center" gap="text-large" fullWidth>
            <SDSText type="button-medium" text="Please do not include sensitive or payment information" color="muted" />
            {/* Announced politely so a screen reader hears the budget shrink
                without interrupting typing. */}
            <div aria-live="polite" aria-atomic="true">
              <SDSText
                id={counterId}
                type="button-small"
                color="inactive"
                text={`${reasonDetail.length}/${REFUND_REASON_DETAIL_MAX_LENGTH}`}
                dataTestId={DataTestIds.BILLING.REFUND.REASON_COUNTER}
              />
            </div>
          </SDSFlexbox>
        </SDSFlexbox>
      ) : null}
    </SDSFlexbox>
  );
}
